import { AppIcon, FeaturedIcon, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { useToolActions } from '@backrs_ui/grow-ui/src/pages/MyColleges/useToolActions';
import type { ComparisonSummary } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, IconButton, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

const STRINGS = appStrings.modules.myColleges.compare;
const BANNER_URL = '/images/compareColleges/compare-colleges-banner.jpg';
const NONE_LOADED = '';

type CompareHeaderProps = Readonly<{
  comparisons: readonly ComparisonSummary[];
  savedId: number | undefined;
  onLoad: (id: number) => void;
  onNew: () => void;
  onDelete: (comparison: ComparisonSummary) => void;
}>;

export const CompareHeader = ({
  comparisons,
  savedId,
  onLoad,
  onNew,
  onDelete,
}: CompareHeaderProps) => {
  const isPhone = useMediaQuery(({ breakpoints }: Theme) => breakpoints.down('md'));
  const actions = useToolActions('compare_colleges', APP_ROUTE.COMPARE_COLLEGES, 'compare');

  return (
    <ResourceHeader
      title={STRINGS.title}
      titleIcon={{
        icon: 'Scales01',
        backgroundColor: 'Brand/700',
        iconColor: 'Colors/Base/White',
      }}
      titleInBanner={
        !isPhone && (
          <Box id="compare-title" sx={titleChipSx}>
            <FeaturedIcon
              iconName="Scales01"
              size="md"
              color="Colors/Base/White"
              backgroundColor="Brand/700"
              borderColor="Gray/700"
            />
            <Typography variant="text-xl-semibold" component="h1">
              {STRINGS.title}
            </Typography>
          </Box>
        )
      }
      banner_media_url={BANNER_URL}
      buttonGroup={actions}
      bannerAction={
        comparisons.length > 0 ? (
          <Box sx={selectSx}>
            <LabeledFilterSelect
              id="compare-saved-comparisons"
              label={STRINGS.savedComparisons}
              iconName="CheckDone02"
              placeholder={STRINGS.savedComparisons}
              options={comparisons.map((summary) => ({
                value: String(summary.id),
                label: summary.name,
              }))}
              value={savedId === undefined ? NONE_LOADED : String(savedId)}
              onChange={(value) => (value === NONE_LOADED ? onNew() : onLoad(Number(value)))}
              optionAction={(option, close) => {
                const comparison = comparisons.find(({ id }) => String(id) === option.value);

                return (
                  comparison && (
                    <IconButton
                      id={`compare-delete-saved-${comparison.id}`}
                      aria-label={STRINGS.deleteSaved(comparison.name)}
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        close();
                        onDelete(comparison);
                      }}
                      sx={deleteSx}
                    >
                      <AppIcon
                        iconName="Trash01"
                        diameter="16px"
                        color="Colors/Foreground/fg-quaternary"
                      />
                    </IconButton>
                  )
                );
              }}
            />
          </Box>
        ) : undefined
      }
    />
  );
};

const titleChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/900']}`,
  backgroundColor: backrs.colors['Beige/25'],
  color: backrs.colors['Gray/900'],
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  width: { xs: '180px', sm: '240px' },
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-md'],
});

const deleteSx: SxProps<Theme> = {
  opacity: 0,
  '.MuiMenuItem-root:hover &, &:focus-visible': { opacity: 1 },
  '@media (hover: none)': { opacity: 1 },
};
